import {
  useUpdateReceiverSessionKey,
  useUpdateReceiversSessionKeys,
} from "apollo/hooks/mutations";
import { useEnvContext } from "context/EnvContext";
import { useSenderAccountContext } from "context/SenderAccountContext";
import {
  CAREPROVIDER_ACTIVE,
  QUERY_PROGRESS_FAILED,
  QUERY_PROGRESS_NOT_STARTED,
  QUERY_PROGRESS_PENDING,
  QUERY_PROGRESS_SUCCEED,
  REQUEST_ACTION_ACCESS_TO_DATA,
  SEARCH_ACTION_SHARE_PATIENT_DATA,
  TRACK_EVENTS,
} from "core/consts";
import { useSafeState } from "core/hooks";
import { importPrivateKey } from "core/model/crypto";
import {
  decryptSessionKey,
  generateExtraSessionKey,
} from "core/model/crypto/cryptoService";
import { getPatientSessionKey } from "core/model/patients/encryption";
import { useIsLoggedSealdOnly } from "core/model/patients/encryption/useIsLoggedSealdOnly";
import { findRequestAction } from "core/model/requests";
import {
  addUsersToSession,
  computeSealdDisplayId,
  getSealdSDKInstance,
} from "core/seald";
import {
  Account,
  Auction,
  AuctionRequest,
  Patient,
  QueryProgress,
  SessionKey,
} from "core/types";
import { EncryptionContextProvider, useEncryption } from "dsl/atoms/Contexts";
import { useToast } from "dsl/atoms/ToastNotificationContext";
import useSearchAction from "dsl/hooks/useSearchAction";
import React, {
  ReactNode,
  useCallback,
  useLayoutEffect,
  useState,
} from "react";
import { useTracking } from "react-tracking";
import { useLoggedInAccount, usePrivateKey } from "reduxentities/hooks";
import { useTranslations } from "translations";
import Translations from "translations/types";
import {
  UseSessionKeyHook,
  fetchKeysdecryptKey,
  getAccounts,
  useGenerateSessionKeysDecryptKey,
} from "./shared";

export function usePatientSessionKeys(): UseSessionKeyHook {
  const isSealdOnly = useIsLoggedSealdOnly();

  const { account, careseeker, privateKey, socialWorkers } =
    useSenderAccountContext();
  const accounts = getAccounts(socialWorkers, account);

  return useGenerateSessionKeysDecryptKey({
    account,
    accounts,
    flag: false, // No need to decrypt the key anymore as encryption is not available at create step anymore
    privateKey,
    skip: isSealdOnly(),
    trackingContext: {
      careseeker_id: careseeker?.id,
    },
    trackingEvent: TRACK_EVENTS.PATIENT_SESSION_KEY_UNAVAILABLE,
  });
}

async function getExtraSessionKey(
  privateKey: string,
  sessionKey: SessionKey,
  context: { id: number; public_key: CryptoKey },
) {
  try {
    const importedPrivateKey = await importPrivateKey(privateKey);
    if (!importedPrivateKey) throw new Error("no private key");

    const decrypted = await decryptSessionKey(
      sessionKey.session_key,
      importedPrivateKey,
      sessionKey?.algorithm,
    );

    if (!decrypted) throw new Error("no decrypted session key");

    return generateExtraSessionKey(decrypted, context);
  } catch (err) {
    console.error("error getting extra cryptokey - ", err);
  }
}

export function getAccountsToGiveAccess({
  auctionRequest,
  patient,
}: {
  auctionRequest: AuctionRequest;
  patient: Patient | null | undefined;
}) {
  const patientSessionKeys = patient?.session_keys || [];

  return (auctionRequest?.accounts || []).filter((account) => {
    if (!account.public_key) return false;
    if (auctionRequest.careprovider?.status !== CAREPROVIDER_ACTIVE)
      return false;

    const withRoles = account.roles?.careprovider_roles?.some(
      (role) => role.careprovider?.id === auctionRequest.careprovider?.id,
    );

    const withAccess =
      patientSessionKeys.find(
        ({ account_id }: { account_id: number }) => account_id === account.id,
      ) != null;

    return withRoles && !withAccess;
  }, []);
}

function getSharePatientWithProgress(
  sharePatientPromise: () => Promise<any>,
  progress: QueryProgress,
  setProgress: (progress: QueryProgress) => void,
  toast: ReturnType<typeof useToast>,
  translations: Translations,
): () => Promise<string | void> {
  return () => {
    if (
      progress == QUERY_PROGRESS_NOT_STARTED ||
      progress == QUERY_PROGRESS_FAILED
    ) {
      setProgress(QUERY_PROGRESS_PENDING);
      return new Promise<string>((resolve, reject) =>
        sharePatientPromise()
          .then(() => {
            setProgress(QUERY_PROGRESS_SUCCEED);
            resolve("");
          })
          .catch((e: unknown) => {
            console.error("Share patient not available:", e);
            setProgress(QUERY_PROGRESS_FAILED);
            toast({
              color: "danger",
              message: translations.auctionRequest.tryAgain,
            });
            reject(e);
          }),
      );
    }
    return Promise.resolve("ok");
  };
}

export function useSharePatientWithReceiver({
  auction,
  auctionRequest,
  trackingContext,
}: {
  auction: Auction | null | undefined;
  auctionRequest: AuctionRequest | null | undefined;
  trackingContext?: Record<string, any>;
}): [() => Promise<string | void>, QueryProgress] {
  const patient = auction?.patient;
  const [progress, setProgress] = useSafeState<QueryProgress>(
    QUERY_PROGRESS_NOT_STARTED,
  );
  const { account: loggedAccount, privateKey } = useSenderAccountContext();
  const { trackEvent } = useTracking();
  const isSealdOnly = useIsLoggedSealdOnly();
  const [mutate] = useUpdateReceiverSessionKey(
    patient?.id ?? -1,
    auctionRequest?.careprovider?.id ?? -1,
  );
  const envContext = useEnvContext();
  const toast = useToast();
  const translations = useTranslations();
  const [sharePatientAction] = useSearchAction({
    actionType: SEARCH_ACTION_SHARE_PATIENT_DATA,
  });

  const sharePatientPromise = useCallback(() => {
    if (!patient || !auctionRequest) return Promise.reject("Null objects");
    const mySessionKey = getPatientSessionKey(loggedAccount, patient);

    if (!mySessionKey) return Promise.reject("You don't have a session key");

    const accountsToAdd = getAccountsToGiveAccess({
      auctionRequest,
      patient,
    });

    const context = {
      auction_request_id: auctionRequest?.id,
      careprovider_id: auctionRequest?.careprovider?.id,
      patient_id: patient?.id,
    };

    if (accountsToAdd.length <= 0) {
      trackEvent({
        name: TRACK_EVENTS.SHARE_PATIENT_WITH_RECEIVER,
        ...context,
        error: "no_accounts",
        status: "error",
      });

      return Promise.reject("No accounts to generate keys for");
    }

    trackEvent({
      name: TRACK_EVENTS.SHARE_PATIENT_WITH_RECEIVER,
      ...context,
      accounts: JSON.stringify(accountsToAdd.map(({ id }) => id)),
      status: "success",
    });

    if (!privateKey) return Promise.reject("no private key");

    const keysToAdd = Promise.all(
      accountsToAdd.map(async (account: any) => {
        return {
          ...(await getExtraSessionKey(privateKey, mySessionKey, {
            id: account.id,
            public_key: account.public_key,
          })),
          patient_id: patient.id,
        };
      }),
    );

    return keysToAdd.then(mutate);
  }, [mutate, patient, auctionRequest, trackingContext]);

  const sharePatientWithSealdPromise = useCallback(async () => {
    try {
      await getSealdSDKInstance();

      const careproviderId = auctionRequest?.careprovider?.id;
      if (!patient || !careproviderId) return Promise.reject("Null objects");
      const patientSealdSessionId = patient.seald_encryption_context?.seald_id;

      if (!patientSealdSessionId) {
        // if the patient was created before Seald was released - eventually should return a Promise.reject
        return;
      }

      trackEvent({
        auction_request_id: auctionRequest.id,
        careprovider_id: auctionRequest.careprovider?.id,
        name: TRACK_EVENTS.SHARE_PATIENT_WITH_RECEIVER_WITH_SEALD,
        patient_id: patient?.id,
        status: "success",
      });

      const careproviderDisplayId = computeSealdDisplayId({
        envContext,
        id: careproviderId,
        type: "careprovider",
      });

      return addUsersToSession({
        createAccessCallback: (entityIds) =>
          sharePatientAction({
            auction,
            context: {
              careprovider_ids: entityIds.map(([, id]) => id),
            },
          }),
        entityUsersDisplayIds: [careproviderDisplayId],
        sessionId: patientSealdSessionId,
      });
    } catch (error) {
      console.error("[sharePatientWithSealdPromise] failed", error);
    }
  }, [mutate, patient, auctionRequest, trackingContext]);

  const combinedSharePatientPromise = useCallback(
    async () =>
      Promise.all([sharePatientPromise(), sharePatientWithSealdPromise()]),
    [sharePatientPromise, sharePatientWithSealdPromise],
  );

  const sharePatientWithProgress = getSharePatientWithProgress(
    isSealdOnly({
      newSealdSession: patient?.seald_encryption_context?.seald_id,
      oldSession: patient?.session_key_context?.has_session_keys,
    })
      ? sharePatientWithSealdPromise
      : combinedSharePatientPromise,
    progress,
    setProgress,
    toast,
    translations,
  );

  return [sharePatientWithProgress, progress];
}

export function useSharePatientWithReceivers({
  auction,
  auctionRequests,
  trackingContext,
}: {
  auction: Auction;
  auctionRequests: Array<AuctionRequest>;
  trackingContext?: Record<string, any>;
}): [() => Promise<string | void>, QueryProgress] {
  const patient = auction.patient;
  const [progress, setProgress] = useSafeState<QueryProgress>(
    QUERY_PROGRESS_NOT_STARTED,
  );
  const { account: loggedAccount, privateKey } = useSenderAccountContext();
  const { trackEvent } = useTracking();
  const isSealdOnly = useIsLoggedSealdOnly();
  const [mutate] = useUpdateReceiversSessionKeys();
  const envContext = useEnvContext();

  const toast = useToast();

  const translations = useTranslations();
  const [sharePatientAction] = useSearchAction({
    actionType: SEARCH_ACTION_SHARE_PATIENT_DATA,
  });

  const sharePatientPromise = useCallback(() => {
    if (!patient || !auctionRequests) return Promise.reject("Null objects");
    const mySessionKey = getPatientSessionKey(loggedAccount, patient);

    if (!mySessionKey) return Promise.reject("You don't have a session key");

    // we need to ensure patient data is only share once per patient/careprovider,
    // especially when we send several requests to same provider for different solutions
    const filteredRequests = auctionRequests.filter(
      ({ careprovider_id }, i) =>
        auctionRequests.findIndex(
          (request) => careprovider_id === request.careprovider_id,
        ) === i,
    );

    const accountsToAdd = filteredRequests.map((auctionRequest) => ({
      accounts: getAccountsToGiveAccess({
        auctionRequest,
        patient,
      }),
      careproviderId: auctionRequest.careprovider?.id,
    }));

    if (accountsToAdd.length <= 0) {
      trackEvent({
        error: "no_accounts",
        name: TRACK_EVENTS.SHARE_PATIENT_WITH_RECEIVERS,
        status: "error",
      });

      return Promise.reject("No accounts to generate keys for");
    }

    trackEvent({
      accounts: JSON.stringify(
        accountsToAdd
          .map(({ accounts }) => accounts)
          .flat()
          .map(({ id }) => id),
      ),
      name: TRACK_EVENTS.SHARE_PATIENT_WITH_RECEIVERS,
      status: "success",
    });

    if (!privateKey) return Promise.reject("no private key");

    return Promise.all(
      accountsToAdd.map(async ({ accounts, careproviderId }) => {
        const keysToAdd = Promise.all(
          accounts.map(async (account: any) => {
            return {
              ...(await getExtraSessionKey(privateKey, mySessionKey, {
                id: account.id,
                public_key: account.public_key,
              })),
              patient_id: patient.id,
            };
          }),
        );

        return keysToAdd.then((keys) =>
          mutate({ variables: { id: careproviderId, input: keys } }),
        );
      }),
    );
  }, [mutate, patient, auctionRequests.length, trackingContext]);

  const sharePatientWithSealdPromise = useCallback(async () => {
    try {
      await getSealdSDKInstance();

      if (!patient || !auctionRequests) return Promise.reject("Null objects");
      const patientSealdSessionId = patient.seald_encryption_context?.seald_id;

      if (!patientSealdSessionId) {
        // if the patient was created before Seald was released - eventually should return a Promise.reject
        return;
      }

      // we need to ensure patient data is only share once per patient/careprovider,
      // especially when we send several requests to same provider for different solutions
      const filteredRequests = auctionRequests.filter(
        ({ careprovider_id }, i) =>
          auctionRequests.findIndex(
            (request) => careprovider_id === request.careprovider_id,
          ) === i,
      );

      const careprovidersToAdd = filteredRequests
        .map((auctionRequest) =>
          auctionRequest?.careprovider_id
            ? computeSealdDisplayId({
                envContext,
                id: auctionRequest.careprovider_id,
                type: "careprovider",
              })
            : undefined,
        )
        .truthy();

      if (!careprovidersToAdd.length) {
        trackEvent({
          error: "no_careproviders",
          name: TRACK_EVENTS.SHARE_PATIENT_WITH_RECEIVERS_WITH_SEALD,
          status: "error",
        });

        return Promise.reject("No careproviders to generate sessions for");
      }

      trackEvent({
        careproviders: JSON.stringify(careprovidersToAdd),
        name: TRACK_EVENTS.SHARE_PATIENT_WITH_RECEIVERS_WITH_SEALD,
        status: "success",
      });

      return addUsersToSession({
        createAccessCallback: (entityIds) =>
          sharePatientAction({
            auction,
            context: {
              careprovider_ids: entityIds.map(([, id]) => id),
            },
          }),
        entityUsersDisplayIds: careprovidersToAdd,
        sessionId: patientSealdSessionId,
      });
    } catch (error) {
      console.error("[sharePatientWithSealdPromise] failed", error);
    }
  }, [mutate, patient, auctionRequests.length, trackingContext]);

  const combinedSharePatientPromise = useCallback(
    async () =>
      Promise.all([sharePatientPromise(), sharePatientWithSealdPromise()]),
    [sharePatientPromise, sharePatientWithSealdPromise],
  );
  const sharePatientWithProgress = getSharePatientWithProgress(
    isSealdOnly({
      newSealdSession: patient?.seald_encryption_context?.seald_id,
      oldSession: patient?.session_key_context?.has_session_keys,
    })
      ? sharePatientWithSealdPromise
      : combinedSharePatientPromise,
    progress,
    setProgress,
    toast,
    translations,
  );

  return [sharePatientWithProgress, progress];
}

export function PatientEncryptionProvider({
  children,
  decryptedSessionKey,
  patient,
}: {
  children: ReactNode;
  decryptedSessionKey?: SessionKey | null;
  patient: Patient | null | undefined;
}) {
  const account = useLoggedInAccount();
  const privateKey = usePrivateKey();
  const isSealdOnly = useIsLoggedSealdOnly();

  const isSealdOnlyPatient = isSealdOnly({
    newSealdSession: patient?.seald_encryption_context?.seald_id,
    oldSession: patient?.session_key_context?.has_session_keys,
  });

  if (!account || (!privateKey && !isSealdOnlyPatient))
    return (
      <EncryptionContextProvider
        value={{
          account,
          decryptedSessionKey: null,
          hasEncryption: false,
          isSealdOnly: isSealdOnlyPatient,
          withEncryptionAccess: false,
        }}
      >
        {children}
      </EncryptionContextProvider>
    );

  // does this function the same as hasEncryption?
  const withEncryptionAccess = isSealdOnlyPatient
    ? !!patient?.seald_encryption_context
    : !!getPatientSessionKey(account, patient);

  if (decryptedSessionKey != null) {
    return (
      <EncryptionContextProvider
        value={{
          account,
          decryptedSessionKey:
            decryptedSessionKey.session_key as unknown as CryptoKey,
          hasEncryption: true,
          isSealdOnly: isSealdOnlyPatient,
          withEncryptionAccess,
        }}
      >
        {children}
      </EncryptionContextProvider>
    );
  }

  if (isSealdOnly() || patient?.seald_encryption_context != null) {
    return (
      <EncryptionContextProvider
        value={{
          account,
          decryptedSessionKey: null,
          hasEncryption: patient?.seald_encryption_context != null,
          isSealdOnly: isSealdOnlyPatient,
          withEncryptionAccess,
        }}
      >
        {children}
      </EncryptionContextProvider>
    );
  }

  const sessionKey = patient?.session_keys?.find(
    (s) => s.account_id == account.id,
  );
  if (!sessionKey || !patient)
    return (
      <EncryptionContextProvider
        value={{
          account,
          decryptedSessionKey: null,
          hasEncryption: false,
          isSealdOnly: isSealdOnlyPatient,
          withEncryptionAccess: false,
        }}
      >
        {children}
      </EncryptionContextProvider>
    );

  return (
    <DecryptPatientSessionKey
      account={account}
      context={{ patient_id: patient.id }}
      isSealdOnly={isSealdOnlyPatient}
      privateKey={privateKey || ""}
      resourceId={patient.id}
      sessionKey={sessionKey}
      withEncryptionAccess={withEncryptionAccess}
    >
      {children}
    </DecryptPatientSessionKey>
  );
}

function DecryptPatientSessionKey({
  account,
  children,
  context,
  isSealdOnly,
  privateKey,
  resourceId,
  sessionKey,
  withEncryptionAccess,
}: {
  account: Account;
  children: React.ReactNode;
  context: Record<string, any>;
  privateKey: string;
  resourceId: number;
  sessionKey: SessionKey;
  isSealdOnly: boolean;
  withEncryptionAccess: boolean;
}) {
  const [accessDenied, setAccessDenied] = useState(false);
  const [decryptedSessionKey, setDecryptedSessionKey] =
    useState<CryptoKey | null>(null);

  const { trackEvent } = useTracking();

  useLayoutEffect(() => {
    fetchKeysdecryptKey({
      context,
      privateKey,
      sessionKey,
      setAccessDenied,
      setDecryptedSessionKey,
      trackEvent,
    });
  }, [resourceId, sessionKey]);

  if (!decryptedSessionKey && !accessDenied) return null;

  return (
    <EncryptionContextProvider
      value={{
        accessDenied,
        account,
        decryptedSessionKey,
        hasEncryption: true,
        isSealdOnly,
        withEncryptionAccess,
      }}
    >
      {children}
    </EncryptionContextProvider>
  );
}

export const useCanShareData = ({
  auctionRequest,
}: {
  auctionRequest: AuctionRequest;
}): {
  canShareData: boolean;
  grantAccessToRedactedFields: boolean | undefined;
} => {
  const { encryptionAvailable, isSealdOnly } = useEncryption();

  if (isSealdOnly) {
    const requestAction = findRequestAction(
      auctionRequest,
      REQUEST_ACTION_ACCESS_TO_DATA,
    );

    return {
      canShareData: !!requestAction,
      grantAccessToRedactedFields:
        requestAction?.context?.variant === "hide_encrypted_fields",
    };
  }

  const availableAccounts = getAccountsToGiveAccess({
    auctionRequest,
    patient: auctionRequest.auction?.patient,
  });

  if (
    !encryptionAvailable ||
    !findRequestAction(auctionRequest, REQUEST_ACTION_ACCESS_TO_DATA) ||
    auctionRequest.auction?.share_patient_data_automatically
  ) {
    return { canShareData: false, grantAccessToRedactedFields: undefined };
  }

  return {
    canShareData: !!availableAccounts?.length,
    grantAccessToRedactedFields: undefined,
  };
};
